import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Loader2 } from 'lucide-react';

interface DataPoint {
  date: string;
  value: number;
  label?: string;
}

interface TrendChartProps {
  title: string;
  data: DataPoint[];
  loading?: boolean;
  color?: 'blue' | 'green' | 'purple' | 'yellow';
  valueLabel?: string;
}

const colorMap = {
  blue: {
    stroke: '#3b82f6',
    fill: 'url(#blueGradient)',
    gradient: ['#3b82f6', 'transparent'],
  },
  green: {
    stroke: '#22c55e',
    fill: 'url(#greenGradient)',
    gradient: ['#22c55e', 'transparent'],
  },
  purple: {
    stroke: '#a855f7',
    fill: 'url(#purpleGradient)',
    gradient: ['#a855f7', 'transparent'],
  },
  yellow: {
    stroke: '#eab308',
    fill: 'url(#yellowGradient)',
    gradient: ['#eab308', 'transparent'],
  },
};

export function TrendChart({
  title,
  data,
  loading = false,
  color = 'blue',
  valueLabel = 'Value',
}: TrendChartProps) {
  const colors = colorMap[color];

  if (loading) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
        <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
        <div className="h-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
      <h3 className="text-lg font-semibold text-white mb-4">{title}</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id={`${color}Gradient`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors.gradient[0]} stopOpacity={0.3} />
                <stop offset="95%" stopColor={colors.gradient[1]} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#334155"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 12 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#64748b', fontSize: 12 }}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
                color: '#fff',
              }}
              labelStyle={{ color: '#94a3b8' }}
              formatter={(value: number) => [value.toLocaleString(), valueLabel]}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={colors.stroke}
              strokeWidth={2}
              fill={colors.fill}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
